import * as L from 'leaflet'
import MapUIConfig, {
  GeoJsonProperties,
  PointFeatureConfig,
  PolygonFeatureConfig,
  PolylineFeatureConfig,
} from '../types/config'
import { compositionToDashArray, getConfigForFeature } from './utils'
import { hideOverflowingPolylineTooltip } from '../utils/map'
import { LineString, Point } from 'geojson'
import { FeatureCollectionWithIds, FeatureWithId } from '../types/geojson'

export const getMarkerOptions = <TProperties extends GeoJsonProperties = GeoJsonProperties, TRenderContext = unknown>(
  featureConfig: PointFeatureConfig<TProperties, TRenderContext>,
  customClassMap?: Record<string, string>,
  customClassName?: string,
): L.MarkerOptions => {
  // Narrow type - check if this is the point type that has a path
  if ('iconFilePath' in featureConfig) {
    return {
      // Question: Do we make the size configurable?
      icon: new L.Icon({
        iconUrl: featureConfig.iconFilePath,
        iconSize: new L.Point(24, 24),
        className: customClassName,
      }),
    }
  }
  const divClassNames = ['c-marker-icon']
  if (featureConfig.markerStyle) {
    divClassNames.push(`c-marker-icon--${featureConfig.markerStyle}`)
  }

  if (featureConfig.customType) {
    if (customClassMap == undefined || !(featureConfig.customType in customClassMap)) {
      throw new Error('MAP-3: Unable to match passed type to a class')
    }

    divClassNames.push(customClassMap[featureConfig.customType])
  }

  if (customClassName) {
    divClassNames.push(customClassName)
  }

  const divClassName = divClassNames.join(' ')

  return {
    icon: L.divIcon({
      className: divClassName,
      html: `${featureConfig.initials}`,
      iconSize: new L.Point(24, 24),
    }),
  }
}

export const getPolylinePathOptions = <
  TProperties extends GeoJsonProperties = GeoJsonProperties,
  TRenderContext = unknown,
>(
  featureConfig: PolylineFeatureConfig<TProperties, TRenderContext>,
): L.PathOptions => {
  return {
    weight: featureConfig.weight ?? 3,
    color: featureConfig.color,
    dashArray: compositionToDashArray(featureConfig.composition, featureConfig.weight ?? 3),
  }
}

export const getPolygonPathOptions = <
  TProperties extends GeoJsonProperties = GeoJsonProperties,
  TRenderContext = unknown,
>(
  featureConfig: PolygonFeatureConfig<TProperties, TRenderContext>,
) => {
  let style: L.PathOptions = {
    color: featureConfig.borderColor,
  }
  if (featureConfig.noFill) {
    style.fillOpacity = 0
  } else {
    style['fillColor'] = featureConfig.fillColor ?? featureConfig.borderColor
    style['fillOpacity'] = featureConfig.fillOpacity ?? 0.6
  }

  style['weight'] = featureConfig.weight ?? 3

  return style
}

export const getFeatureStyle = <TProperties extends GeoJsonProperties = GeoJsonProperties, TRenderContext = unknown>(
  config: MapUIConfig<TProperties, TRenderContext>,
  feature: FeatureWithId<TProperties>,
): L.PathOptions => {
  const featureConfig = getConfigForFeature(config, feature)

  if (featureConfig.geometryType === 'LineString') {
    return getPolylinePathOptions(featureConfig)
  }

  if (featureConfig.geometryType === 'Polygon') {
    return getPolygonPathOptions(featureConfig)
  }

  return {}
}

export const styleFeatureLayer = <TProperties extends GeoJsonProperties = GeoJsonProperties, TRenderContext = unknown>(
  layer: L.GeoJSON,
  config: MapUIConfig<TProperties, TRenderContext>,
) => {
  layer.setStyle(((feature: FeatureWithId<TProperties>) =>
    getFeatureStyle(config, feature)) as L.StyleFunction<TProperties>)
}

export const createFeatureLayer = <TProperties extends GeoJsonProperties = GeoJsonProperties, TRenderContext = unknown>(
  config: MapUIConfig<TProperties, TRenderContext>,
  features: FeatureCollectionWithIds<TProperties>,
  customClassMap?: Record<string, string>,
) =>
  L.geoJSON(features, {
    style: ((feature: FeatureWithId<TProperties>) => getFeatureStyle(config, feature)) as L.StyleFunction<TProperties>,
    onEachFeature: (feature: FeatureWithId<TProperties>, layer) => {
      const featureConfig = getConfigForFeature(config, feature)
      if (featureConfig?.geometryType == 'Polygon' && featureConfig.contentPropertyKey) {
        const key = featureConfig.contentPropertyKey as keyof TProperties
        const content =
          feature.properties && key in feature.properties ? (feature.properties as NonNullable<TProperties>)[key] : null
        if (content) {
          layer
            .bindTooltip(`${content}`, {
              className: `c-polygon-label`,
              permanent: true,
              direction: 'center',
              opacity: 0,
            })
            .on('add', () => {
              const map: L.Map | undefined = (layer as any)?._map
              if (!map) return

              // Hide the tooltip now if its overflowing
              hideOverflowingPolylineTooltip(map, layer as L.Polyline<LineString, TProperties>)

              // Add a zoom handler to check the size each zoom.
              const onZoom = () => hideOverflowingPolylineTooltip(map, layer as L.Polyline<LineString, TProperties>)

              map.on('zoom', onZoom)
              layer.on('remove', () => {
                map.off('zoom', onZoom)
              })
            })
        }
      }
    },
    pointToLayer: (feature: FeatureWithId<TProperties, Point>) => {
      const featureConfig = getConfigForFeature(config, feature)
      if (featureConfig.geometryType === 'Point') {
        return L.marker(
          { lng: feature.geometry.coordinates[0], lat: feature.geometry.coordinates[1] },
          getMarkerOptions(featureConfig, customClassMap),
        )
      }

      throw new Error('MAP-2: Trying to render a non-point feature to a layer')
    },
  })
